/**
 * ひきつぎコード(docs/10_paltown_platform.md §3.4)。
 *
 * とうろくしていないゲスト(匿名UID)は、端末や URL が変わると別人になる。
 * メールを持たない子どもが本人だけで続きを遊べるように、**8文字のコード**で
 * サーバー側のデータを新しい UID へ合流させる。
 *
 *   つくる側(古い端末・旧URL) … `issue()` → 「ABCD-2345」を大きく表示
 *   いれる側(新しい端末・新URL)… `redeem(code)` → サーバーが合流 → 読み戻して「おかえり!」
 *
 * **このファイルは Firebase SDK を import しない。**
 * 画面(`ui/AccountScreen.tsx`)はここの型と文言しか知らないので、
 * ローカルモードのバンドルに SDK が1バイトも混ざらない(CLAUDE.md ルール6)。
 * 実装は `firebase/transferService.ts`(動的 import チャンクの向こう側)。
 *
 * 合流そのものは**ぜんぶサーバーの1トランザクション**(CLAUDE.md ルール2)。
 * クライアントは「コードを見せる」「コードを渡す」「読み戻す」しかしない。
 */

/** 発行できたコード。 */
export interface TransferCode {
  /** 画面に出す形(`ABCD-2345`)。 */
  readonly code: string;
  /** 有効期限(epoch ms)。 */
  readonly expiresAt: number;
}

/** 合流できたときの、画面の「おかえり!」用の中身。 */
export interface TransferResult {
  /** 元の記録の星の合計。 */
  readonly stars: number;
  /** 元の記録の通貨別コイン。 */
  readonly coins: Readonly<Record<string, number>>;
}

/**
 * 失敗の種別。
 *
 * `invalid-code` は「無い・違う・期限切れ・使用済み」を**ぜんぶ同じ**にしてある
 * (サーバーが区別を返さない = 総当たりでコードを探れない)。
 */
export type TransferErrorCode =
  | 'invalid-code'
  | 'too-many'
  /** とうろくずみ(匿名でない)。ログインで引き継げるので、そちらへ案内する。 */
  | 'signed-in'
  | 'network'
  /** ローカルモード等、そもそも使えない。 */
  | 'unsupported'
  | 'unknown';

export class TransferError extends Error {
  constructor(
    readonly code: TransferErrorCode,
    override readonly cause?: unknown,
  ) {
    super(code);
    this.name = 'TransferError';
  }
}

/** 画面に出す文言(ひらがな中心。CLAUDE.md ルール5)。 */
const MESSAGES: Readonly<Record<TransferErrorCode, string>> = {
  'invalid-code': 'コードが ちがうか、きげんぎれだよ',
  'too-many': 'すこし じかんを おいてね',
  'signed-in': 'とうろくずみの ひとは ログインで ひきつげるよ',
  network: 'インターネットに つながらないみたい。つながってから もういちど ためしてね',
  unsupported: 'この かんきょうでは つかえません',
  unknown: 'うまく いかなかったよ。もういちど ためしてね',
};

export function transferErrorMessage(error: unknown): string {
  if (error instanceof TransferError) return MESSAGES[error.code];
  return MESSAGES.unknown;
}

export interface TransferService {
  /** 使えるか(ローカルモードでは false = 導線ごと出さない)。 */
  readonly supported: boolean;
  /** ひきつぎコードを発行する(ゲストのみ)。 */
  issue(): Promise<TransferCode>;
  /**
   * ひきつぎコードを使って、元の記録を いまの UID に合流させる(ゲストのみ)。
   * 成功したら**読み戻しまで終えてから**返る(画面はそのあとキャッシュを読むだけ)。
   */
  redeem(code: string): Promise<TransferResult>;
}

/**
 * ローカルモード用(= 何もできない)実装。
 * `supported: false` なので画面はカードごと出さない。
 */
export class NoTransferService implements TransferService {
  readonly supported = false;

  async issue(): Promise<TransferCode> {
    throw new TransferError('unsupported');
  }

  async redeem(): Promise<TransferResult> {
    throw new TransferError('unsupported');
  }
}

// ── 画面のことば・入力の整え(純関数。テストしやすいようにここへ置く)──────

/** コードに使う32文字(0/O/1/I は入っていない。docs/10 §3.4)。 */
export const TRANSFER_CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';

/** ハイフンを除いたコードの長さ。 */
export const TRANSFER_CODE_LENGTH = 8;

/**
 * 全角で打たれた文字を半角へ寄せる(サーバーの `normalizeTransferCode` と同じ規則)。
 *
 * 日本語 IME をオンにしたまま打つと `ＡＢＣＤ－２３４５` のように全角で入る。
 * 「IME を切ってね」と言わずに済むよう、`Ａ-Ｚ` `ａ-ｚ` `０-９` は半角へ、
 * 長音符 `ー` ・全角ハイフン `－` ・ダッシュ類は半角ハイフンとして扱う。
 */
function toHalfWidthCode(input: string): string {
  return (
    input
      .replace(/[\uFF21-\uFF3A\uFF41-\uFF5A\uFF10-\uFF19]/g, (char) =>
        String.fromCharCode(char.charCodeAt(0) - 0xfee0),
      )
      // ー(長音符) / －(全角ハイフン) / ‐〜―(ダッシュ類) / −(全角マイナス)。
      .replace(/[\u30FC\uFF0D\u2010-\u2015\u2212]/g, '-')
  );
}

/**
 * 入力欄の中身を整える(打っているあいだ毎回かかる)。
 *
 * 全角は半角に、小文字は大文字に、コードに使わない文字は捨て、4文字を超えたら
 * 自動でハイフンを入れる(見せている形と同じにする)。
 * 判定そのものはサーバーがやるので、ここは**打ちやすさのため**だけ。
 */
export function formatCodeInput(raw: string): string {
  let body = '';
  for (const char of toHalfWidthCode(raw).toUpperCase()) {
    if (body.length >= TRANSFER_CODE_LENGTH) break;
    if (TRANSFER_CODE_ALPHABET.includes(char)) body += char;
  }
  if (body.length <= 4) return body;
  return `${body.slice(0, 4)}-${body.slice(4)}`;
}

/** 8文字そろったか(「ひきつぐ」ボタンを押せるか)。 */
export function isCompleteCode(value: string): boolean {
  return formatCodeInput(value).replace('-', '').length === TRANSFER_CODE_LENGTH;
}

/** 有効期限の言い方(「7にちかん つかえるよ」)。 */
export const TRANSFER_LABELS = {
  cardTitle: '🔁 ひきつぎ',
  intro:
    'べつの たんまつや、あたらしい ばしょで つづきを あそぶための コードだよ。おうちのひとと いっしょに やってね。',
  signedIn: 'とうろくずみの ひとは ログインで ひきつげるよ',
  issue: 'ひきつぎコードを つくる',
  redeem: 'ひきつぎコードを いれる',
  submit: 'ひきつぐ',
  copy: 'コピー',
  copied: 'コピーしたよ',
  codeHint: '7にちかん つかえるよ。あたらしい ばしょで このコードを いれてね',
  inputLabel: 'ひきつぎコード(8もじ)',
  notice: 'ひきついだあとも、まえの ばしょの データは のこるけど、べつべつの きろくに なるよ',
  busy: 'まってね…',
} as const;

/**
 * ひきつぎカードの出しかた。
 *
 *   `hidden`  … カードごと出さない(ローカルモード = 仕組みが無い)
 *   `guest`   … ゲスト。つくる・いれる の両方を出す
 *   `signedIn`… とうろくずみ。「ログインで ひきつげるよ」の一文だけ
 */
export type TransferCardMode = 'hidden' | 'guest' | 'signedIn';

export interface TransferCardContext {
  /** ひきつぎが使えるか(ローカルモードでは false)。 */
  readonly supported: boolean;
  /** 匿名でない実アカウントで入っているか。 */
  readonly signedIn: boolean;
}

export function transferCardMode({ supported, signedIn }: TransferCardContext): TransferCardMode {
  if (!supported) return 'hidden';
  return signedIn ? 'signedIn' : 'guest';
}

/** 合流できたときの「おかえり!」。コインはタイピングのぶんを出す。 */
export function welcomeBackText(result: TransferResult): string {
  const coins = Math.max(0, Math.trunc(result.coins.typing ?? 0));
  const stars = Math.max(0, Math.trunc(result.stars));
  return `おかえり! ★${stars} と コイン ${coins} を ひきついだよ`;
}
